import React, {useEffect, useRef} from "react";
import {Animated, StyleSheet, View, Text, Easing, Platform} from "react-native";
import Svg, {Rect} from "react-native-svg";
import {PALETTE} from "./lib/palette";

/**
 * Session Expired(마커 없는 만료 — URL 직접입력·손상 QR) 전용 화면.
 * "만료 → 다시 스캔"을 살린 카메라 뷰파인더 디자인: QR 글리프 + accent 코너 브래킷 + 스캔 라인 애니메이션.
 * prefers-reduced-motion이면 스캔 라인 정지.
 */
const QR_INK = "#232830";

function QrGlyph({size}: {size: number}) {
    return (
        <Svg width={size} height={size} viewBox="0 0 100 100">
            {/* finder patterns */}
            <Rect x={2} y={2} width={28} height={28} rx={4} fill={QR_INK}/>
            <Rect x={8} y={8} width={16} height={16} rx={2} fill="#ffffff"/>
            <Rect x={12} y={12} width={8} height={8} rx={1.5} fill={QR_INK}/>
            <Rect x={70} y={2} width={28} height={28} rx={4} fill={QR_INK}/>
            <Rect x={76} y={8} width={16} height={16} rx={2} fill="#ffffff"/>
            <Rect x={80} y={12} width={8} height={8} rx={1.5} fill={QR_INK}/>
            <Rect x={2} y={70} width={28} height={28} rx={4} fill={QR_INK}/>
            <Rect x={8} y={76} width={16} height={16} rx={2} fill="#ffffff"/>
            <Rect x={12} y={80} width={8} height={8} rx={1.5} fill={QR_INK}/>
            {/* data modules */}
            {[
                [38, 4], [54, 4], [46, 14], [62, 14], [38, 24], [54, 24],
                [4, 38], [20, 38], [38, 38], [62, 38], [88, 38],
                [12, 48], [30, 48], [54, 48], [72, 48], [88, 48],
                [4, 58], [46, 58], [62, 58], [80, 58],
                [38, 70], [54, 70], [72, 70], [88, 70],
                [46, 80], [70, 80], [88, 80], [38, 90], [60, 90], [80, 90],
            ].map(([x, y], i) => (
                <Rect key={i} x={x} y={y} width={7} height={7} rx={1.4} fill={QR_INK}/>
            ))}
        </Svg>
    );
}

export default function SessionExpired({accent}: {accent: string}) {
    const scan = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        const reduce = Platform.OS === "web" && typeof window !== "undefined" && !!window.matchMedia
            && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (reduce) return;
        const loop = Animated.loop(Animated.timing(scan, {
            toValue: 1, duration: 2400, easing: Easing.inOut(Easing.quad), useNativeDriver: false,
        }));
        loop.start();
        return () => loop.stop();
    }, []);

    const scanStyle = {
        transform: [{translateY: scan.interpolate({inputRange: [0, 1], outputRange: [10, 112]})}],
        opacity: scan.interpolate({inputRange: [0, 0.12, 0.88, 1], outputRange: [0, 1, 1, 0]}),
    };

    return (
        <View style={styles.wrap}>
            <View style={styles.viewfinder}>
                <QrGlyph size={94}/>
                {/* corner brackets */}
                <View style={[styles.br, styles.tl, {borderColor: accent}]}/>
                <View style={[styles.br, styles.tr, {borderColor: accent}]}/>
                <View style={[styles.br, styles.bl, {borderColor: accent}]}/>
                <View style={[styles.br, styles.brr, {borderColor: accent}]}/>
                {/* scan line */}
                <Animated.View style={[styles.scan, {backgroundColor: accent, shadowColor: accent}, scanStyle]}/>
            </View>

            <Text style={styles.title}>Session expired</Text>
            <Text style={styles.msg}>Point your camera at the table QR code to start ordering.</Text>
        </View>
    );
}

const styles = StyleSheet.create({
    wrap: {
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        paddingHorizontal: 24,
    },
    viewfinder: {
        width: 132,
        height: 132,
        alignItems: "center",
        justifyContent: "center",
    },
    br: {
        position: "absolute",
        width: 26,
        height: 26,
    },
    tl: {top: 0, left: 0, borderTopWidth: 3, borderLeftWidth: 3, borderTopLeftRadius: 4},
    tr: {top: 0, right: 0, borderTopWidth: 3, borderRightWidth: 3, borderTopRightRadius: 4},
    bl: {bottom: 0, left: 0, borderBottomWidth: 3, borderLeftWidth: 3, borderBottomLeftRadius: 4},
    brr: {bottom: 0, right: 0, borderBottomWidth: 3, borderRightWidth: 3, borderBottomRightRadius: 4},
    scan: {
        position: "absolute",
        top: 0,
        left: 10,
        right: 10,
        height: 3,
        borderRadius: 3,
        opacity: 0.9,
        shadowOpacity: 0.5,
        shadowRadius: 6,
        shadowOffset: {width: 0, height: 0},
    },
    title: {
        fontSize: 21,
        fontWeight: "800",
        letterSpacing: -0.3,
        color: PALETTE.TEXT,
        marginTop: 28,
        textAlign: "center",
    },
    msg: {
        fontSize: 13.5,
        lineHeight: 21,
        color: PALETTE.MUTED,
        marginTop: 10,
        textAlign: "center",
        maxWidth: 220,
    },
});
